import { Toggle } from "@ark-ui/react";
import { ArrowLeft, List, X } from "@phosphor-icons/react";
import clsx from "clsx";
import * as React from "react";
import { logos } from "../../assets/index.js";
import { IconButton } from "../icon-button/IconButton.js";
import { Text } from "../text/Text.js";

interface BaseAppbarProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  logo?: string;
  logoAlt?: string;
  actions?: React.ReactNode;
  variant: "default" | "page";
  classNames?: {
    root?: string;
    leftSection?: string;
    leftSectionLogo?: string;
    rightSection?: string;
    leftSectionIcon?: string;
  };
}

interface HamburgerAppbarProps extends BaseAppbarProps {
  withHamburger: true;
  onHamburgerClick: (pressed?: boolean) => void;
  hamburgerState?: boolean;
}

interface NonHamburgerAppbarProps extends BaseAppbarProps {
  withHamburger?: false;
  onHamburgerClick?: never;
  hamburgerState?: never;
}

interface PageAppbarProps extends BaseAppbarProps {
  variant: "page";
  title: string;
  icon: React.ReactElement;
  withIcon?: boolean;
  onBackClick?: () => void;
}

interface DefaultAppbarProps extends BaseAppbarProps {
  variant: "default";
  title?: never;
  icon?: never;
  withIcon?: never;
  onBackClick?: never;
}

type AppbarProps = (HamburgerAppbarProps | NonHamburgerAppbarProps) &
  (DefaultAppbarProps | PageAppbarProps);

const Appbar = React.forwardRef<HTMLDivElement, AppbarProps>((props, ref) => {
  const {
    className,
    children,
    logo = logos.product.hdfcLife,
    logoAlt = "HDFC Life",
    classNames,
    withHamburger = false,
    title,
    icon,
    actions,
    onHamburgerClick,
    hamburgerState,
    onBackClick,
    withIcon = false,
    variant = "default",
    ...rest
  } = props;

  const [hamburger, setHumburger] = React.useState(false);
  const isHamburgerOpen = hamburgerState ?? hamburger;

  const handleHamburgerClick = React.useCallback(
    (pressed?: boolean) => {
      if (withHamburger) {
        onHamburgerClick?.(pressed);

        if (hamburgerState === undefined) {
          setHumburger(pressed ?? !isHamburgerOpen);
        }
      }
    },
    [onHamburgerClick, withHamburger, hamburgerState, isHamburgerOpen],
  );

  const handleBackClick = React.useCallback(() => {
    if (withHamburger) {
      onBackClick?.();
    }
  }, [onBackClick, withIcon]);

  return (
    <div
      ref={ref}
      className={clsx("ox-reset ox-Appbar", className, classNames?.root)}
      {...rest}
    >
      <div
        className={clsx(
          "ox-reset ox-AppbarLeftSection",
          classNames?.leftSection,
        )}
      >
        {withHamburger && variant === "default" ? (
          <Toggle.Root
            onPressedChange={(pressed) => {
              handleHamburgerClick(pressed);
            }}
            asChild
            className={clsx("ox-reset AppbarHamburger")}
          >
            <IconButton variant="tertiary" color="gray">
              {!isHamburgerOpen ? <List /> : <X />}
            </IconButton>
          </Toggle.Root>
        ) : null}

        {variant === "default" ? (
          <img
            src={logo}
            alt={logoAlt}
            className={clsx(
              "ox-reset ox-AppbarLeftSectionLogo",
              classNames?.leftSectionLogo,
            )}
          />
        ) : null}

        {variant === "page" && icon ? (
          <IconButton
            variant="tertiary"
            color="gray"
            className={clsx(
              "ox-reset ox-AppbarLeftSectionIcon",
              classNames?.leftSectionIcon,
            )}
            onClick={handleBackClick}
          >
            {icon}
          </IconButton>
        ) : withIcon ? (
          <IconButton
            variant="tertiary"
            color="gray"
            className={clsx(
              "ox-reset ox-AppbarLeftSectionIcon",
              classNames?.leftSectionIcon,
            )}
            onClick={handleBackClick}
          >
            <ArrowLeft />
          </IconButton>
        ) : null}

        {variant === "page" && title && (
          <Text size="lg" fontWeight="medium">
            {title}
          </Text>
        )}
      </div>
      {actions ? (
        <div
          className={clsx(
            "ox-reset ox-AppbarRightSection",
            classNames?.rightSection,
          )}
        >
          {actions}
        </div>
      ) : null}
    </div>
  );
});

Appbar.displayName = "Appbar";
export { Appbar };
export type { AppbarProps };
